/* Shared content-type filter bar used by FeedPage and QueuePage.
   Content type is a Select dropdown; tag filter and special tabs render alongside. */

import { useState, useMemo } from "react";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ArrowDownNarrowWide, ArrowUpNarrowWide, MonitorPlay, Podcast, Clapperboard, FileText, LayoutGrid, Sparkles, Tag, Hash, X } from "lucide-react";

interface ContentTypeOption {
	value: string;
	label: string;
	icon: React.ComponentType<{ className?: string }>;
}

const CONTENT_TYPES: ContentTypeOption[] = [
	{ value: "all", label: "All", icon: LayoutGrid },
	{ value: "videos", label: "Videos", icon: MonitorPlay },
	{ value: "articles", label: "Articles", icon: FileText },
	{ value: "podcasts", label: "Podcasts", icon: Podcast },
	{ value: "reels", label: "Shorts", icon: Clapperboard },
];

interface ContentTypeTabsProps {
	activeTab: string;
	onActiveTabChange: (value: string) => void;
	provocativeCount?: number;
	showForYou?: boolean;
	filterTag?: string | null;
	onFilterTagChange?: (tag: string | null) => void;
	availableTags?: string[];
	filterTopic?: string | null;
	onFilterTopicChange?: (topic: string | null) => void;
	availableTopics?: string[];
	sortOrder?: "asc" | "desc";
	onSortOrderChange?: (order: "asc" | "desc") => void;
}

export function ContentTypeTabs({
	activeTab,
	onActiveTabChange,
	provocativeCount,
	showForYou,
	filterTag,
	onFilterTagChange,
	availableTags,
	filterTopic,
	onFilterTopicChange,
	availableTopics,
	sortOrder,
	onSortOrderChange,
}: ContentTypeTabsProps) {
	const showProvocative = provocativeCount != null && provocativeCount > 0;
	const showTagFilter = availableTags != null && availableTags.length > 0;
	const showTopicFilter = availableTopics != null && availableTopics.length > 0;
	const showSortOrder = sortOrder != null && onSortOrderChange != null;
	const isContentType = CONTENT_TYPES.some((ct) => ct.value === activeTab);

	return (
		<div className="px-4 md:px-6 overflow-x-auto overflow-y-hidden scrollbar-none flex items-center gap-2">
			<div className="relative shrink-0">
				<Select value={isContentType ? activeTab : "all"} onValueChange={onActiveTabChange}>
					<SelectTrigger size="sm" className="w-auto min-w-[100px]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{CONTENT_TYPES.map((ct) => (
							<SelectItem key={ct.value} value={ct.value}>
								<ct.icon className="size-4" />
								{ct.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{!isContentType && (
					<button
						type="button"
						className="absolute inset-0 cursor-pointer"
						onClick={() => onActiveTabChange("all")}
						aria-label="Switch to All"
					/>
				)}
			</div>

			{showForYou && (
				<>
					<div className="h-5 w-px bg-border shrink-0" />
					<TabsList variant="default" className="bg-transparent">
						<TabsTrigger value="for_you" tabIndex={-1} className="group/foryou">
							<Sparkles className="size-3.5 group-hover/foryou:text-primary group-data-active/foryou:text-primary transition-colors" />
							For You
						</TabsTrigger>
					</TabsList>
				</>
			)}

			{showProvocative && (
				<>
					<div className="h-5 w-px bg-border shrink-0" />
					<TabsList variant="default" className="bg-transparent">
						<TabsTrigger
							value="provocative"
							tabIndex={-1}
							className="group/prov data-active:text-primary data-active:bg-primary/10"
						>
							<Sparkles className="size-3.5 group-hover/prov:text-primary group-data-active/prov:text-primary transition-colors" />
							Provocative ({provocativeCount})
						</TabsTrigger>
					</TabsList>
				</>
			)}

			{showTagFilter && (
				<>
					<div className="h-5 w-px bg-border shrink-0" />
					<TagFilterButton
						filterTag={filterTag ?? null}
						onFilterTagChange={onFilterTagChange}
						availableTags={availableTags}
					/>
				</>
			)}

			{showTopicFilter && (
				<>
					<div className="h-5 w-px bg-border shrink-0" />
					<TopicFilterButton
						filterTopic={filterTopic ?? null}
						onFilterTopicChange={onFilterTopicChange}
						availableTopics={availableTopics}
					/>
				</>
			)}

			{showSortOrder && (
				<button
					type="button"
					className="ml-auto gap-1.5 shrink-0 rounded-md px-1.5 py-0.5 text-sm font-medium inline-flex items-center whitespace-nowrap text-foreground/60 hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground cursor-pointer transition-colors [&_svg]:pointer-events-none [&_svg]:shrink-0"
					onClick={() => onSortOrderChange(sortOrder === "asc" ? "desc" : "asc")}
					aria-label={sortOrder === "asc" ? "Sorted oldest first, click to sort newest first" : "Sorted newest first, click to sort oldest first"}
				>
					{sortOrder === "asc" ? (
						<ArrowUpNarrowWide className="size-4" />
					) : (
						<ArrowDownNarrowWide className="size-4" />
					)}
					{sortOrder === "asc" ? "Oldest" : "Newest"}
				</button>
			)}
		</div>
	);
}

interface TagFilterButtonProps {
	filterTag: string | null;
	onFilterTagChange?: (tag: string | null) => void;
	availableTags: string[];
}

function TagFilterButton({ filterTag, onFilterTagChange, availableTags }: TagFilterButtonProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const filteredTags = useMemo(() => {
		if (!search.trim()) return availableTags;
		const lower = search.toLowerCase();
		return availableTags.filter((tag) => tag.toLowerCase().includes(lower));
	}, [availableTags, search]);

	function handleSelect(tag: string) {
		onFilterTagChange?.(tag);
		setOpen(false);
		setSearch("");
	}

	function handleClear(e: React.MouseEvent) {
		e.stopPropagation();
		onFilterTagChange?.(null);
	}

	if (filterTag) {
		return (
			<button
				type="button"
				className="gap-1.5 shrink-0 rounded-md px-1.5 py-0.5 text-sm font-medium inline-flex items-center whitespace-nowrap bg-background text-foreground border border-input shadow-sm cursor-pointer transition-colors hover:bg-accent [&_svg]:pointer-events-none [&_svg]:shrink-0"
				onClick={handleClear}
			>
				<Tag className="size-4" />
				{filterTag}
				<X className="size-4" />
			</button>
		);
	}

	return (
		<Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="gap-1.5 shrink-0 rounded-md px-1.5 py-0.5 text-sm font-medium inline-flex items-center whitespace-nowrap text-foreground/60 hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground cursor-pointer transition-colors [&_svg]:pointer-events-none [&_svg]:shrink-0"
				>
					<Tag className="size-4" />
					Tag
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-56 p-0">
				<div className="p-2 border-b border-border">
					<Input
						placeholder="Search tags..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="h-8 text-sm"
						autoFocus
					/>
				</div>
				<div className="max-h-48 overflow-y-auto p-1">
					{filteredTags.length === 0 ? (
						<p className="text-xs text-muted-foreground px-2 py-3 text-center">
							No tags found
						</p>
					) : (
						filteredTags.map((tag) => (
							<button
								key={tag}
								type="button"
								className="w-full text-left text-sm px-2 py-1.5 rounded-md hover:bg-accent transition-colors cursor-pointer"
								onClick={() => handleSelect(tag)}
							>
								{tag}
							</button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}

interface TopicFilterButtonProps {
	filterTopic: string | null;
	onFilterTopicChange?: (topic: string | null) => void;
	availableTopics: string[];
}

function TopicFilterButton({ filterTopic, onFilterTopicChange, availableTopics }: TopicFilterButtonProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const filteredTopics = useMemo(() => {
		if (!search.trim()) return availableTopics;
		const lower = search.toLowerCase();
		return availableTopics.filter((topic) => topic.toLowerCase().includes(lower));
	}, [availableTopics, search]);

	function handleSelect(topic: string) {
		onFilterTopicChange?.(topic);
		setOpen(false);
		setSearch("");
	}

	function handleClear(e: React.MouseEvent) {
		e.stopPropagation();
		onFilterTopicChange?.(null);
	}

	if (filterTopic) {
		return (
			<button
				type="button"
				className="gap-1.5 shrink-0 rounded-md px-1.5 py-0.5 text-sm font-medium inline-flex items-center whitespace-nowrap bg-background text-foreground border border-input shadow-sm cursor-pointer transition-colors hover:bg-accent [&_svg]:pointer-events-none [&_svg]:shrink-0"
				onClick={handleClear}
			>
				<Hash className="size-4" />
				{filterTopic}
				<X className="size-4" />
			</button>
		);
	}

	return (
		<Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="gap-1.5 shrink-0 rounded-md px-1.5 py-0.5 text-sm font-medium inline-flex items-center whitespace-nowrap text-foreground/60 hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground cursor-pointer transition-colors [&_svg]:pointer-events-none [&_svg]:shrink-0"
				>
					<Hash className="size-4" />
					Topic
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-56 p-0">
				<div className="p-2 border-b border-border">
					<Input
						placeholder="Search topics..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="h-8 text-sm"
						autoFocus
					/>
				</div>
				<div className="max-h-48 overflow-y-auto p-1">
					{filteredTopics.length === 0 ? (
						<p className="text-xs text-muted-foreground px-2 py-3 text-center">
							No topics found
						</p>
					) : (
						filteredTopics.map((topic) => (
							<button
								key={topic}
								type="button"
								className="w-full text-left text-sm px-2 py-1.5 rounded-md hover:bg-accent transition-colors cursor-pointer"
								onClick={() => handleSelect(topic)}
							>
								{topic}
							</button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
